import { TFunction } from 'i18next';
import { ErrorMapperFactory } from './errors/mappers';
import { NormalizedError, ErrorCode } from '../types/errors';
import { logError } from './monitoring/faro';
import { getErrorMapperSource } from './api';

export enum ErrorCategory {
  ROUTE_NOT_FOUND = 'ROUTE_NOT_FOUND',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN = 'UNKNOWN'
}

// Map error codes to categories
const codeToCategory = new Map<string, ErrorCategory>([
  [ErrorCode.ROUTE_NOT_FOUND, ErrorCategory.ROUTE_NOT_FOUND],
  [ErrorCode.RESOURCE_NOT_FOUND, ErrorCategory.RESOURCE_NOT_FOUND],
  [ErrorCode.UNAUTHORIZED, ErrorCategory.AUTHENTICATION_ERROR],
  [ErrorCode.FORBIDDEN, ErrorCategory.AUTHENTICATION_ERROR],
  [ErrorCode.AUTH_INVALID, ErrorCategory.AUTHENTICATION_ERROR],
  [ErrorCode.AUTH_EXPIRED, ErrorCategory.AUTHENTICATION_ERROR],
  [ErrorCode.VALIDATION, ErrorCategory.VALIDATION_ERROR],
  [ErrorCode.CONTENT_INVALID, ErrorCategory.VALIDATION_ERROR],
  [ErrorCode.SERVER_ERROR, ErrorCategory.SERVER_ERROR],
  [ErrorCode.EXTERNAL_SERVICE_ERROR, ErrorCategory.SERVER_ERROR],
  [ErrorCode.EXTERNAL_SERVICE_TIMEOUT, ErrorCategory.SERVER_ERROR],
  [ErrorCode.NETWORK_ERROR, ErrorCategory.NETWORK_ERROR],
]);

export interface ErrorDetails {
  category: ErrorCategory;
  messageKey: string;
  normalizedError: NormalizedError;
}

const normalizeError = (error: unknown, source?: string): NormalizedError => {
  // If source is not provided, try to determine it automatically
  const mapperSource = source || getErrorMapperSource(error);
  const mapper = ErrorMapperFactory.getMapper(mapperSource);
  return mapper.normalize(error);
};

const categorizeError = (error: NormalizedError): ErrorCategory => {
  return codeToCategory.get(error.code) || ErrorCategory.UNKNOWN;
};

export const getErrorDetails = (error: unknown, source?: string): ErrorDetails => {
  const normalizedError = normalizeError(error, source);
  const category = categorizeError(normalizedError);
  
  return {
    category,
    messageKey: `errors.${category.toLowerCase()}`,
    normalizedError
  };
};

export const getUserFriendlyError = (error: unknown, t: TFunction, source?: string): string => {
  // If the error is already a translation key, use it directly
  if (typeof error === 'string' && error.startsWith('errors.')) {
    return t(error, { defaultValue: t('errors.somethingWentWrong') });
  }
  if (error instanceof Error && error.message.startsWith('errors.')) {
    return t(error.message, { defaultValue: t('errors.somethingWentWrong') });
  }

  // Normalize and get the error details
  const { normalizedError, category } = getErrorDetails(error, source);
  
  // Log the error with original message and translation key
  if (error instanceof Error) {
    logError(error, normalizedError.message, {
      category,
      originalMessage: error.message,
      source,
      ...(error.stack && { stack: error.stack })
    });
  }

  // Try to get a specific error message first
  const specificMessage = t(normalizedError.message, { defaultValue: '' });
  if (specificMessage) {
    return specificMessage;
  }

  // If no specific message, use category-based message
  const categoryMessage = t(`errors.${category.toLowerCase()}`, { defaultValue: '' });
  if (categoryMessage) {
    return categoryMessage;
  }

  // Final fallback
  return t('errors.somethingWentWrong', { defaultValue: 'Something went wrong' });
};

export const isErrorCategory = (error: unknown, category: ErrorCategory, source?: string): boolean => {
  const details = getErrorDetails(error, source);
  return details.category === category;
}; 